import * as THREE from 'three';
import { createSky, sampleSky } from './day-cycle.js';

/**
 * What the world is like right now, for everything that dresses it: lamps that come on at dusk, wind that gusts, mist that
 * lies over the coast, rain. One shared, mutable record, written once a frame by the scene and read by props, materials and
 * critters, so no prop needs to know about the clock. Pure functions below work out each value from the in-game clock.
 */
export interface Conditions {
  /** 0 in daylight, 1 in full night: lamps, windows and the lighthouse follow it, and the wildlife keeps its hours by it. */
  night: number;
  /** 0–1 sea mist lying over the shore and low ground, thickest around dawn. */
  mist: number;
  /** 0–1 rainfall. */
  rain: number;
  /** 0–1 how wet the ground is: it lingers after the rain. */
  wet: number;
  /** Multiplies how hard the wind bends grass and branches; 1 is the everyday breeze. */
  wind: number;
  /** 0–1 how much cloud there is, for the cloud shadows sliding over the land. */
  cover: number;
}
export const conditions: Conditions = { night: 0, mist: 0, rain: 0, wet: 0, wind: 1, cover: .4 };

/** Shared with every lamp material and glow halo so a single value brings the whole map's lights up. */
export const nightUniform = { value: 0 };
/** Shared with every weathered prop material: how wet surfaces are (`conditions.wet`). */
export const wetUniform = { value: 0 };
/** Presentation seconds for lamp flicker, written by the map's animation each frame (it stops while the game is paused). */
export const lampTime = { value: 0 };
/** Shared with every wind material. */
export const windScale = { value: 1 };

const smooth = THREE.MathUtils.smoothstep;
const sky = createSky();

/** How dark it is at `clock` (minutes since the first midnight): lamps are lit from about 18:30 and out by about 06:30. */
export function nightness(clock: number): number {
  sampleSky(clock, sky);
  return 1 - smooth(sky.keyIntensity, .5, 2.5);
}

/** A repeatable pseudo-random number in [0, 1) from a day, a purpose and the map's seed. */
function roll(day: number, purpose: number, seed: number): number {
  let h = Math.imul(day + 1013, 0x9e3779b1) ^ Math.imul(purpose + 17, 0x85ebca6b) ^ Math.imul(seed | 0, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d); h = Math.imul(h ^ (h >>> 12), 0x297a2d39); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

const ramp = (value: number, from: number, to: number) => THREE.MathUtils.clamp((value - from) / (to - from), 0, 1);
/** 0 → 1 over `up` minutes from `start`, held, then 1 → 0 over `down` minutes ending at `end`. */
const envelope = (minute: number, start: number, end: number, up: number, down: number) => Math.min(ramp(minute, start, start + up), 1 - ramp(minute, end - down, end));

/** Rainfall at a clock time: some days have one fall, starting in the afternoon or evening more often than not. */
function rainAt(clock: number, seed: number): number {
  const day = Math.floor(clock / 1440), minute = clock - day * 1440;
  if (roll(day, 1, seed) > .38) return 0;
  const start = (9 + roll(day, 2, seed) * 11) * 60, length = 60 + roll(day, 3, seed) * 150, peak = .45 + roll(day, 4, seed) * .55;
  return peak * envelope(minute, start, start + length, 30, 40);
}

export interface Weather { mist: number; rain: number; wet: number; wind: number; cover: number }

/**
 * The weather at `clock`, a pure function of the clock and a seed (the map's), so every player sees the same sky without the
 * server knowing anything about it. Sea mist comes most mornings and burns off by mid-morning; rain falls on roughly a third
 * of days; the wind gusts every half hour or so and blows hard in rain; the ground stays wet long after the rain has stopped.
 */
export function weatherAt(clock: number, seed: number): Weather {
  const day = Math.floor(clock / 1440), minute = clock - day * 1440;
  const mist = roll(day, 5, seed) < .55
    ? (.4 + roll(day, 6, seed) * .6) * envelope(minute, (4 + roll(day, 7, seed)) * 60, (8 + roll(day, 8, seed) * 2.5) * 60, 60, 100) : 0;
  const rain = rainAt(clock, seed);
  const wet = Math.max(rain, .7 * rainAt(clock - 40, seed), .4 * rainAt(clock - 100, seed), mist * .35);
  // Gusts every 41 minutes or so, and a breeze that rises and falls over hours; neither depends on the day, so the wind never jumps at midnight.
  const phase = ((clock + (seed % 41)) / 41) % 1, gust = Math.sin(Math.PI * Math.min(1, phase / .22)) ** 2;
  const wind = .85 + .3 * Math.sin(clock * .013) + .15 * Math.sin(clock * .0071 + seed) + rain * .9 + gust * .8;
  const cover = Math.min(1, .35 + .2 * Math.sin(clock * .0021 + seed) + rain * .7 + mist * .3);
  return { mist, rain, wet, wind, cover };
}

/** A forced sky, for studying one kind of weather at any hour. */
export type WeatherPreset = 'clear' | 'mist' | 'rain' | 'storm';
export function presetWeather(preset: WeatherPreset): Weather {
  if (preset === 'rain') return { mist: .2, rain: .5, wet: .8, wind: 1.5, cover: .9 };
  if (preset === 'storm') return { mist: .25, rain: 1, wet: 1, wind: 2.6, cover: 1 };
  if (preset === 'mist') return { mist: .9, rain: 0, wet: .4, wind: .8, cover: .55 };
  return { mist: 0, rain: 0, wet: 0, wind: 1, cover: .3 };
}

/** Writes the shared record and uniforms for `clock`; `forced` overrides the weather (not the night). */
export function setConditions(clock: number, seed: number, forced?: WeatherPreset | null) {
  const weather = forced ? presetWeather(forced) : weatherAt(clock, seed);
  conditions.night = nightness(clock);
  conditions.mist = weather.mist; conditions.rain = weather.rain; conditions.wet = weather.wet; conditions.wind = weather.wind; conditions.cover = weather.cover;
  nightUniform.value = conditions.night; windScale.value = conditions.wind; wetUniform.value = conditions.wet;
}

/**
 * A lamp's glass: bright at night, dull in daylight when the flame is lost against the sun. Every lamp and lit window
 * shares one patched program and the night uniform. `flicker` (0–0.2 or so) is how much the flame behind the glass
 * wavers after dark; each lamp wavers on its own phase, taken from where it stands.
 */
export function lampMaterial(color: number, flicker = 0) {
  const material = new THREE.MeshBasicMaterial({ color }), amount = { value: flicker };
  material.customProgramCacheKey = () => 'lamp-glass';
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, { uNight: nightUniform, uLampTime: lampTime, uFlicker: amount });
    shader.vertexShader = 'varying float vLampPhase;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vLampPhase = dot(modelMatrix[3].xz, vec2(.37, .61));');
    shader.fragmentShader = 'uniform float uNight, uLampTime, uFlicker;\nvarying float vLampPhase;\n' + shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
  float waver = sin(uLampTime * 7.3 + vLampPhase) * .45 + sin(uLampTime * 12.9 + vLampPhase * 2.3) * .3 + sin(uLampTime * 3.1 + vLampPhase * .7) * .35;
  diffuseColor.rgb *= mix(.3, 1. + uFlicker * waver, uNight);`);
  };
  return material;
}

/** Makes a glow halo's brightness follow the night, however its owner animates its opacity. */
export function nightGlow<T extends THREE.SpriteMaterial>(material: T): T {
  material.customProgramCacheKey = () => 'lamp-halo';
  material.onBeforeCompile = shader => {
    shader.uniforms.uNight = nightUniform;
    shader.fragmentShader = 'uniform float uNight;\n' + shader.fragmentShader.replace('#include <alphatest_fragment>', '#include <alphatest_fragment>\n  diffuseColor.a *= mix(.12, 1., uNight);');
  };
  return material;
}
